import { Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { InactividadDialogComponent } from '@/shared/dialogs/inactividad-dialog/inactividad-dialog.component';
import { AuthService } from './auth/auth.service';

@Injectable({ providedIn: 'root' })
export class InactivityService {
  private authService: AuthService;
  private inactivityTimeout?: ReturnType<typeof setTimeout>;
  private countdownInterval?: ReturnType<typeof setInterval>;
  private forceLogoutTimeout?: ReturnType<typeof setTimeout>;
  private isModalOpen = false;
  private monitoringPaused = false;
  private lastActivityTimestamp = Date.now();

  private INACTIVITY_LIMIT_MS = 1 * 60 * 1000; // ⏱ 1 minuto
  private COUNTDOWN_SECONDS = 60;              // ⏳ cuenta regresiva

  private readonly EVENTS = [
    'click', 'mousedown', 'mouseup', 
    'mousemove', 'keydown', 'keypress', 'keyup',
    'scroll', 'touchstart', 'touchmove', 'touchend',
    'wheel', 'input', 'paste', 'cut'
  ];

  private readonly boundResetTimer = this.handleUserActivity.bind(this);
  private dialogActivityListener?: () => void;

  constructor(
    private dialog: MatDialog,
    private injector: Injector
  ) {}

  private getAuthService(): AuthService {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    return this.authService;
  }

  public configure(timeoutMinutes: number, countdownSeconds: number): void {
    this.INACTIVITY_LIMIT_MS = timeoutMinutes * 60 * 1000;
    this.COUNTDOWN_SECONDS = countdownSeconds;
  }

  public startMonitoring(): void {
    this.monitoringPaused = false;
    this.resetInactivityTimer();
    this.addActivityListeners();
  }

  public stopMonitoring(): void {
    this.removeActivityListeners();
    this.clearAllTimers();
    this.isModalOpen = false;
  }

  public pauseMonitoring(): void {
    this.monitoringPaused = true;
    this.clearAllTimers();
  }

  public resumeMonitoring(): void {
    this.monitoringPaused = false;
    this.resetInactivityTimer('resume');
  }

  public extendSession(): void {
    this.lastActivityTimestamp = Date.now();
    this.resetInactivityTimer('manual-extension');
  }

  private handleUserActivity(event: Event): void {
    if (this.monitoringPaused || this.isModalOpen) return;

    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTimestamp;

    if (timeSinceLastActivity < 200) return;

    this.lastActivityTimestamp = now;
    this.resetInactivityTimer(event.type);
  }

  private resetInactivityTimer(source?: string): void {
    if (this.monitoringPaused) return;

    this.clearAllTimers();

    this.inactivityTimeout = setTimeout(() => {
      this.showInactividadDialog();
    }, this.INACTIVITY_LIMIT_MS);
  }

  private showInactividadDialog(): void {
    if (this.isModalOpen || this.monitoringPaused) return;
    this.isModalOpen = true;

    const dialogRef = this.dialog.open(InactividadDialogComponent, {
      disableClose: true,
      width: '380px',
      data: { seconds: this.COUNTDOWN_SECONDS },
      panelClass: 'inactivity-dialog'
    });

    const countdownStart = Date.now();

    this.countdownInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - countdownStart) / 1000);
      const secondsLeft = this.COUNTDOWN_SECONDS - elapsed;

      if (dialogRef.componentInstance) {
        dialogRef.componentInstance.data.seconds = secondsLeft;
      }

      if (secondsLeft <= 0) {
        clearInterval(this.countdownInterval);
      }
    }, 1000);

    this.forceLogoutTimeout = setTimeout(() => {
      dialogRef.close(false);
    }, this.COUNTDOWN_SECONDS * 1000);

    this.dialogActivityListener = () => {
      // No actualiza la sesión, solo previene errores
    };
    window.addEventListener('mousemove', this.dialogActivityListener, { passive: true });
    window.addEventListener('keydown', this.dialogActivityListener, { passive: true });

    dialogRef.afterClosed().subscribe(response => {
      this.cleanupDialogResources();

      if (response === true) {
        this.resetInactivityTimer('dialog-closed-by-activity');
      } else {
        this.forceLogout();
      }
    });
  }

  private cleanupDialogResources(): void {
    this.isModalOpen = false;
    this.clearAllTimers();

    if (this.dialogActivityListener) {
      window.removeEventListener('mousemove', this.dialogActivityListener);
      window.removeEventListener('keydown', this.dialogActivityListener);
      this.dialogActivityListener = undefined;
    }
  }

  private forceLogout(): void {
    this.stopMonitoring();
    this.getAuthService().logout();
  }

  private clearAllTimers(): void {
    clearTimeout(this.inactivityTimeout);
    clearInterval(this.countdownInterval);
    clearTimeout(this.forceLogoutTimeout);
  }

  private addActivityListeners(): void {
    this.EVENTS.forEach(event =>
      window.addEventListener(event, this.boundResetTimer, { passive: true })
    );
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  private removeActivityListeners(): void {
    this.EVENTS.forEach(event =>
      window.removeEventListener(event, this.boundResetTimer)
    );
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      const now = Date.now();
      const timePassed = now - this.lastActivityTimestamp;

      if (!this.isModalOpen && !this.monitoringPaused && timePassed >= this.INACTIVITY_LIMIT_MS) {
        this.showInactividadDialog();
      }
    }
  }
}
